import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { usePaletteStore } from '@repletoryreach/sdk-utils/store';
import { Avatar, Stack, Text } from '@repletoryreach/sdk-ui';
import { useT } from '../../i18n/I18nProvider';
import { DOCS_URL, STATUS_URL } from '../external-links';
import './ProfileMenu.css';

/**
 * Avatar trigger in the top-right that opens a dropdown with account info,
 * settings shortcuts, helpers, and Sign out. Implemented as a plain HTML
 * button + a portal-rendered dropdown so the open/close path is explicit
 * (no cloneElement prop-merging, no Menu-component surprises).
 */
export function ProfileMenu() {
  const t = useT();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const openPalette = usePaletteStore((s) => s.openPalette);

  const [opened, setOpened] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Two initials where a full name gives them, so the avatar reads as a person.
  const source = user?.display_name || user?.email || '?';
  const initials = source.includes(' ')
    ? source.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
    : source.slice(0, 1).toUpperCase();

  // Position the dropdown right-aligned below the trigger.
  useEffect(() => {
    if (!opened || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, [opened]);

  // Close on outside click, Esc, scroll. Escape also hands focus back to the
  // trigger, so a keyboard user is not dropped at the top of the document.
  useEffect(() => {
    if (!opened) return;
    function onPointer(e: MouseEvent) {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setOpened(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      setOpened(false);
      anchorRef.current?.focus();
    }
    function onScroll() {
      setOpened(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [opened]);

  // A `role="menu"` that cannot be walked with the arrow keys is a menu in name
  // only, so the panel takes focus when it opens and the arrows move between
  // its items.
  useEffect(() => {
    if (!opened) return;
    const first = dropdownRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
    first?.focus();
  }, [opened, coords]);

  function onMenuKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']; // i18n-ignore: KeyboardEvent.key values
    if (!keys.includes(e.key)) return;
    const items = Array.from(
      dropdownRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []
    );
    if (items.length === 0) return;
    e.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === 'Home' ? 0
      : e.key === 'End' ? items.length - 1
      : e.key === 'ArrowDown' ? (current + 1) % items.length
      : (current - 1 + items.length) % items.length;
    items[next]?.focus();
  }

  function go(path: string) {
    setOpened(false);
    navigate(path);
  }

  function openExternal(url: string) {
    setOpened(false);
    window.open(url, '_blank', 'noopener');
  }

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={opened}
        aria-label={t('shell.profile.open')}
        className="rr-profile-menu__trigger"
        onClick={() => setOpened((v) => !v)}
      >
        <Avatar
          size="sm"
          radius="xl"
          style={{ background: 'var(--rr-color-primary)', color: 'var(--rr-color-primary-text)', fontWeight: 700 }}
        >
          {initials}
        </Avatar>
        {/* The person's name, with the address only as a fallback. An email in
            the header is an account detail; the name is who is signed in. */}
        <span className="rr-profile-menu__email">{user?.display_name || user?.email || t('shell.profile.guest')}</span>
        <ChevronDown />
      </button>

      {opened && coords && createPortal(
        <div
          ref={dropdownRef}
          role="menu"
          aria-label={t('shell.profile.menu')}
          className="rr-profile-menu__dropdown"
          style={{ top: coords.top, right: coords.right }}
          onKeyDown={onMenuKeyDown}
        >
          <div className="rr-profile-menu__summary">
            <Stack gap={2}>
              <Text role="bodySmall" fw={700}>{user?.display_name ?? user?.email}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{user?.email}</Text>
              {user?.org?.name && (
                <Text role="caption" c="var(--rr-color-text-muted)">{user.org.name}</Text>
              )}
            </Stack>
          </div>

          <MenuDivider />

          <MenuLabel>{t('users.view.account.title')}</MenuLabel>
          <MenuItem icon={<UserIcon />} onClick={() => go('/settings/profile')}>{t('settings.layout.nav.profile')}</MenuItem>
          <MenuItem icon={<ShieldIcon />} onClick={() => go('/settings/security')}>{t('settings.layout.nav.security')}</MenuItem>
          <MenuItem icon={<PaletteIcon />} onClick={() => go('/settings/appearance')}>{t('settings.layout.nav.appearance')}</MenuItem>
          {/* "Notifications" used to sit here pointing at /settings/profile,
              the same destination as the Profile row two lines above. One
              label per destination. */}
          <MenuItem icon={<CogIcon />} onClick={() => go('/settings')}>{t('shell.profile.allSettings')}</MenuItem>

          <MenuDivider />

          <MenuLabel>{t('shell.profile.quickActions')}</MenuLabel>
          <MenuItem
            icon={<SearchIcon />}
            right={<kbd className="rr-profile-menu__kbd">⌘K</kbd>}
            onClick={() => { setOpened(false); openPalette(); }}
          >
            {t('shell.profile.commandPalette')}
          </MenuItem>
          {/* Both of these were literal *.repletoryreach.local hosts that resolve
              nowhere, and "What's new" opened the raw platform log viewer. The
              two that can be real are configuration now and only render when
              they point somewhere; the changelog entry is gone until there is
              a changelog. */}
          {DOCS_URL !== '' && (
            <MenuItem icon={<HelpIcon />} onClick={() => openExternal(DOCS_URL)}>
              {t('shell.help.aria')}
            </MenuItem>
          )}
          {STATUS_URL !== '' && (
            <MenuItem icon={<DocIcon />} onClick={() => openExternal(STATUS_URL)}>
              {t('shell.profile.statusPage')}
            </MenuItem>
          )}

          <MenuDivider />

          <MenuItem
            tone="error"
            icon={<LogoutIcon />}
            onClick={async () => {
              setOpened(false);
              await logout();
              navigate('/auth/login', { replace: true });
            }}
          >
            {t('action.signOut')}
          </MenuItem>
        </div>,
        document.body
      )}
    </>
  );
}

interface MenuItemProps {
  icon?: ReactNode;
  right?: ReactNode;
  onClick: () => void;
  tone?: 'default' | 'error';
  children: ReactNode;
}
function MenuItem({ icon, right, onClick, tone = 'default', children }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`rr-profile-menu__item rr-profile-menu__item--${tone}`}
      onClick={onClick}
    >
      {icon && <span className="rr-profile-menu__item-icon">{icon}</span>}
      <span className="rr-profile-menu__item-label">{children}</span>
      {right && <span className="rr-profile-menu__item-right">{right}</span>}
    </button>
  );
}

function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="rr-profile-menu__group-label">{children}</div>;
}

function MenuDivider() {
  return <div className="rr-profile-menu__divider" role="separator" />;
}

function ChevronDown() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="rr-profile-menu__chevron">
      <polyline points="3,5 6,8 9,5" />
    </svg>
  );
}

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}
function UserIcon() { return <Icon><path d="M8 8a3 3 0 100-6 3 3 0 000 6z" /><path d="M3 14a5 5 0 0110 0" /></Icon>; }
function ShieldIcon() { return <Icon><path d="M8 1.5l5.5 2.4v3.6c0 4-5.5 7-5.5 7s-5.5-3-5.5-7V3.9L8 1.5z" /></Icon>; }
function PaletteIcon() { return <Icon><circle cx="8" cy="8" r="2" /><path d="M8 2v3M8 11v3M2 8h3M11 8h3M3.5 3.5l2 2M10.5 10.5l2 2M3.5 12.5l2-2M10.5 5.5l2-2" /></Icon>; }
function CogIcon() { return <Icon><path d="M2.5 5h11M2.5 8h11M2.5 11h7" /></Icon>; }
function SearchIcon() { return <Icon><circle cx="7" cy="7" r="3.5" /><line x1="9.5" y1="9.5" x2="13" y2="13" /></Icon>; }
function HelpIcon() { return <Icon><circle cx="8" cy="8" r="6" /><path d="M6 6.5a2 2 0 014 0c0 1.5-2 1.5-2 3" /><circle cx="8" cy="12" r="0.5" fill="currentColor" /></Icon>; }
function DocIcon() { return <Icon><path d="M3 2h7l3 3v9H3z" /><path d="M10 2v3h3M5.5 8h5M5.5 10.5h5" /></Icon>; }
function LogoutIcon() { return <Icon><path d="M9.5 5V3.5a1 1 0 00-1-1h-5a1 1 0 00-1 1v9a1 1 0 001 1h5a1 1 0 001-1V11" /><path d="M7 8h7M11.5 5.5L14 8l-2.5 2.5" /></Icon>; }
